import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, forwardRef, useContext, useEffect, useId, useMemo, useRef, useState, } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Portal, anchoredStyle, useAnchoredPosition } from '../lib/overlay.js';
import { uiStrings } from '../strings.js';
import { InlineError } from './feedback.js';
const FieldContext = createContext(null);
/** Denetimin Field bağlamı (yoksa null): id, aria-describedby, aria-invalid, required. */
export function useFieldControl() {
    return useContext(FieldContext);
}
export function Label({ htmlFor, children, required, optional, className, id, }) {
    return (_jsxs("label", { htmlFor: htmlFor, id: id, className: cn('block text-base font-semibold text-fg', className), children: [children, required ? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", className: "ml-0.5 text-danger", children: "*" }), _jsxs("span", { className: "sr-only", children: [" (", uiStrings.required, ")"] })] })) : null, optional ? (_jsxs("span", { className: "ml-1 text-sm font-normal text-fg-subtle", children: ["(", uiStrings.optional, ")"] })) : null] }));
}
/**
 * Form alanı sarmalayıcısı. Hata metni `aria-describedby` ile denetime bağlanır; denetim `aria-invalid` alır.
 * Denetim (Input, Textarea, Select, Combobox) bağlamı kendisi okur.
 */
export function Field({ label, children, description, error, required = false, optional = false, id, className, hideLabel = false, }) {
    const auto = useId();
    const controlId = id ?? `f${auto.replace(/:/g, '')}`;
    const descId = description ? `${controlId}-desc` : undefined;
    const errId = error ? `${controlId}-err` : undefined;
    const describedBy = [descId, errId].filter(Boolean).join(' ') || undefined;
    const value = useMemo(() => ({ id: controlId, describedBy, invalid: Boolean(error), required }), [controlId, describedBy, error, required]);
    return (_jsxs("div", { className: cn('space-y-1.5', className), children: [_jsx(Label, { htmlFor: controlId, required: required, optional: optional, className: hideLabel ? 'sr-only' : undefined, children: label }), _jsx(FieldContext.Provider, { value: value, children: children }), description ? (_jsx("p", { id: descId, className: "text-sm text-fg-subtle", children: description })) : null, error ? _jsx(InlineError, { id: errId, children: error }) : null] }));
}
// Tema uyarlaması: denetim 40 px (dokunmatik 44), yarıçap 6, etiket üstte 14/600 (Label)
const controlBase = cn('w-full rounded-control border bg-surface text-base text-fg placeholder:text-fg-subtle', 'transition-colors duration-fast', 'focus:border-focus focus:outline-none focus:ring-1 focus:ring-focus', 'disabled:cursor-not-allowed disabled:bg-disabled disabled:text-disabled-foreground');
/** Salt okunur yalnız metin denetimlerinde görünür (select her zaman `:read-only` eşleşir) */
const textReadOnly = 'read-only:bg-surface-subtle';
function controlState(invalid) {
    return invalid ? 'border-danger' : 'border-border-strong';
}
function useControlProps(p) {
    const f = useFieldControl();
    const invalid = p['aria-invalid'] === true || p['aria-invalid'] === 'true' || (f?.invalid ?? false);
    const describedBy = [f?.describedBy, p['aria-describedby']].filter(Boolean).join(' ') || undefined;
    return {
        id: p.id ?? f?.id,
        'aria-describedby': describedBy,
        'aria-invalid': invalid || undefined,
        required: p.required ?? f?.required,
        invalid,
    };
}
export const Input = forwardRef(function Input({ className, ...rest }, ref) {
    const { invalid, ...aria } = useControlProps(rest);
    return (_jsx("input", { ref: ref, ...rest, ...aria, className: cn(controlBase, textReadOnly, 'h-control min-h-control px-3', controlState(invalid), className) }));
});
/** Tarih/saat: tarayıcının yerel seçicisi (erişilebilir ve yerelleştirilmiş); biçim değeri ISO'dur. */
export const DateTimeInput = forwardRef(function DateTimeInput({ kind = 'date', className, ...rest }, ref) {
    return _jsx(Input, { ref: ref, type: kind, className: cn('tabular-nums', className), ...rest });
});
export const Textarea = forwardRef(function Textarea({ className, rows = 3, ...rest }, ref) {
    const { invalid, ...aria } = useControlProps(rest);
    return (_jsx("textarea", { ref: ref, rows: rows, ...rest, ...aria, className: cn(controlBase, textReadOnly, 'px-3 py-2 leading-5', controlState(invalid), className) }));
});
export const Select = forwardRef(function Select({ className, children, compact = false, ...rest }, ref) {
    const { invalid, ...aria } = useControlProps(rest);
    return (_jsxs("div", { className: cn('relative', className), children: [_jsx("select", { ref: ref, ...rest, ...aria, className: cn(controlBase, compact
                    ? 'h-control-sm min-h-control-sm appearance-none pl-2 pr-7 text-sm'
                    : 'h-control min-h-control appearance-none pl-3 pr-8', controlState(invalid)), children: children }), _jsx(ChevronDown, { size: 16, "aria-hidden": "true", className: cn('pointer-events-none absolute top-1/2 -translate-y-1/2 text-fg-muted', compact ? 'right-2' : 'right-2.5') })] }));
});
export const Checkbox = forwardRef(function Checkbox({ label, description, indeterminate = false, className, id, ...rest }, ref) {
    const inner = useRef(null);
    const setRefs = (el) => {
        inner.current = el;
        if (typeof ref === 'function')
            ref(el);
        else if (ref)
            ref.current = el;
    };
    useEffect(() => {
        if (inner.current)
            inner.current.indeterminate = indeterminate;
    }, [indeterminate]);
    const auto = useId();
    const cid = id ?? `c${auto.replace(/:/g, '')}`;
    const descId = description ? `${cid}-desc` : undefined;
    const box = (_jsx("input", { ref: setRefs, id: cid, type: "checkbox", "aria-describedby": descId, className: cn('h-[18px] w-[18px] shrink-0 cursor-pointer rounded-sm border-border-strong align-middle accent-primary', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface', 'disabled:cursor-not-allowed', label ? 'mt-0.5' : '', className), ...rest }));
    if (!label)
        return box;
    return (_jsxs("div", { className: "flex items-start gap-2", children: [box, _jsxs("div", { className: "min-w-0", children: [_jsx("label", { htmlFor: cid, className: "cursor-pointer text-base text-fg", children: label }), description ? (_jsx("p", { id: descId, className: "text-sm text-fg-subtle", children: description })) : null] })] }));
});
export function RadioGroup({ legend, name, value, onChange, options, description, className, orientation = 'vertical', variant = 'default', descriptionOnMobile = 'show', }) {
    const auto = useId().replace(/:/g, '');
    if (variant === 'segmented') {
        const descId = description ? `rd${auto}` : undefined;
        return (_jsxs("fieldset", { className: cn('flex flex-wrap items-center gap-x-3 gap-y-1', className), "aria-describedby": descId, children: [_jsx("legend", { className: "float-left mr-1 text-sm font-medium text-fg-muted", children: legend }), _jsx("div", { className: "inline-flex rounded-pill border border-border bg-surface-subtle p-1", children: options.map((o) => {
                        const rid = `r${auto}-${o.value}`;
                        return (_jsxs("div", { className: "relative", children: [_jsx("input", { id: rid, type: "radio", name: name, value: o.value, checked: value === o.value, disabled: o.disabled, onChange: () => {
                                        onChange(o.value);
                                    }, className: "peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed" }), _jsx("label", { htmlFor: rid, className: cn('pointer-events-none flex min-h-[2.75rem] items-center rounded-pill px-4 text-base font-medium text-fg-muted sm:min-h-[2.25rem]', 'transition-[background-color,color,box-shadow] duration-fast', 'peer-checked:bg-surface peer-checked:text-primary peer-checked:shadow-card', 'peer-hover:text-fg peer-focus-visible:ring-2 peer-focus-visible:ring-focus', 'peer-disabled:opacity-60'), children: o.label })] }, o.value));
                    }) }), description ? (_jsx("p", { id: descId, className: cn('max-w-xs text-sm text-fg-muted', descriptionOnMobile === 'hide' && 'sr-only sm:not-sr-only'), children: description })) : null] }));
    }
    return (_jsxs("fieldset", { className: cn('space-y-1.5', className), children: [_jsx("legend", { className: "text-base font-semibold text-fg", children: legend }), description ? _jsx("p", { className: "text-sm text-fg-subtle", children: description }) : null, _jsx("div", { className: cn('flex gap-x-4 gap-y-1.5', orientation === 'vertical' ? 'flex-col' : 'flex-wrap'), children: options.map((o) => {
                    const rid = `r${auto}-${o.value}`;
                    return (_jsxs("div", { className: "flex items-start gap-2", children: [_jsx("input", { id: rid, type: "radio", name: name, value: o.value, checked: value === o.value, disabled: o.disabled, onChange: () => {
                                    onChange(o.value);
                                }, className: "mt-px h-[18px] w-[18px] shrink-0 cursor-pointer accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed" }), _jsxs("div", { className: "min-w-0", children: [_jsx("label", { htmlFor: rid, className: "cursor-pointer text-base text-fg", children: o.label }), o.description ? _jsx("p", { className: "text-sm text-fg-subtle", children: o.description }) : null] })] }, o.value));
                }) })] }));
}
/** Açık/kapalı ayarı: `role="switch"`; etkisi anında olan tercihler için (form gönderimi gerektiren değer → Checkbox). */
export function Switch({ checked, onChange, label, description, disabled = false, id, }) {
    const auto = useId().replace(/:/g, '');
    const sid = id ?? `s${auto}`;
    const descId = description ? `${sid}-desc` : undefined;
    return (_jsxs("div", { className: "flex items-start gap-3", children: [_jsx("button", { id: sid, type: "button", role: "switch", "aria-checked": checked, "aria-describedby": descId, disabled: disabled, onClick: () => {
                    onChange(!checked);
                }, className: cn('relative inline-flex h-6 w-11 shrink-0 items-center rounded-pill border transition-colors duration-fast', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface', 'disabled:cursor-not-allowed disabled:opacity-60', checked ? 'border-primary bg-primary' : 'border-border-strong bg-surface-subtle'), children: _jsx("span", { "aria-hidden": "true", className: cn('inline-block h-[18px] w-[18px] rounded-full bg-surface shadow-none ring-1 ring-border-strong transition-transform duration-fast', checked ? 'translate-x-[22px] ring-primary' : 'translate-x-[2px]') }) }), _jsxs("div", { className: "min-w-0", children: [_jsx("label", { htmlFor: sid, className: "cursor-pointer text-base text-fg", children: label }), description ? (_jsx("p", { id: descId, className: "text-sm text-fg-subtle", children: description })) : null] })] }));
}
const trLower = (s) => s.normalize('NFC').toLocaleLowerCase('tr-TR').trim();
export function Combobox({ options, onSelect, createLabel, onCreate, placeholder, clearOnSelect = true, disabled = false, emptyText = uiStrings.noOptions, id, normalize = trLower, }) {
    const f = useFieldControl();
    const auto = useId().replace(/:/g, '');
    const inputId = id ?? f?.id ?? `cb${auto}`;
    const listId = `${inputId}-list`;
    const [text, setText] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const q = normalize(text);
    const filtered = q.length === 0 ? options : options.filter((o) => normalize(o.label).includes(q));
    const exact = options.some((o) => normalize(o.label) === q);
    const canCreate = Boolean(onCreate && createLabel && q.length > 0 && !exact);
    const rows = [
        ...filtered.map((option) => ({ kind: 'option', option })),
        ...(canCreate ? [{ kind: 'create' }] : []),
    ];
    const activeIndex = Math.min(active, Math.max(rows.length - 1, 0));
    const optionId = (i) => `${listId}-o${i}`;
    useEffect(() => {
        if (!open)
            return;
        function onDown(e) {
            const t = e.target;
            if (!wrapRef.current?.contains(t) && !listRef.current?.contains(t))
                setOpen(false);
        }
        document.addEventListener('pointerdown', onDown);
        return () => {
            document.removeEventListener('pointerdown', onDown);
        };
    }, [open]);
    function choose(i) {
        const row = rows[i];
        if (!row)
            return;
        if (row.kind === 'create')
            onCreate?.(text.trim());
        else
            onSelect(row.option.value);
        if (clearOnSelect)
            setText('');
        setOpen(false);
        setActive(0);
    }
    function onKeyDown(e) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open)
                setOpen(true);
            else
                setActive((a) => Math.min(a + 1, rows.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
        }
        else if (e.key === 'Enter') {
            if (open && rows.length > 0) {
                e.preventDefault();
                choose(activeIndex);
            }
        }
        else if (e.key === 'Escape') {
            if (open) {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
            }
            else if (text)
                setText('');
        }
        else if (e.key === 'Home' && open) {
            setActive(0);
        }
        else if (e.key === 'End' && open) {
            setActive(rows.length - 1);
        }
    }
    const expanded = open && !disabled;
    const pos = useAnchoredPosition(expanded, inputRef, listRef, 'start');
    return (_jsxs("div", { ref: wrapRef, className: "relative", children: [_jsx("input", { ref: inputRef, id: inputId, type: "text", role: "combobox", "aria-autocomplete": "list", "aria-expanded": expanded, "aria-controls": listId, "aria-activedescendant": expanded && rows.length > 0 ? optionId(activeIndex) : undefined, "aria-describedby": f?.describedBy, "aria-invalid": f?.invalid ? true : undefined, autoComplete: "off", disabled: disabled, placeholder: placeholder, value: text, onChange: (e) => {
                    setText(e.target.value);
                    setOpen(true);
                    setActive(0);
                }, onFocus: () => {
                    setOpen(true);
                }, onKeyDown: onKeyDown, className: cn(controlBase, 'h-control min-h-control px-3', controlState(f?.invalid ?? false)) }), _jsx(Portal, { anchorRef: inputRef, children: _jsx("ul", { ref: listRef, id: listId, role: "listbox", hidden: !expanded, style: anchoredStyle(pos, { matchAnchorWidth: true }), className: "fixed z-popover max-h-60 max-w-[calc(100vw-1rem)] overflow-auto rounded-card border border-border bg-surface-raised p-1 shadow-overlay", children: rows.length === 0 ? (_jsx("li", { role: "presentation", className: "px-3 py-2 text-base text-fg-subtle", children: emptyText })) : (rows.map((row, i) => (_jsx("li", { id: optionId(i), role: "option", "aria-selected": i === activeIndex, onPointerDown: (e) => {
                            e.preventDefault();
                        }, onClick: () => {
                            choose(i);
                        }, onMouseMove: () => {
                            setActive(i);
                        }, className: cn('flex min-h-control cursor-pointer items-center gap-2 rounded-md px-3 text-base text-fg', i === activeIndex ? 'bg-primary-subtle' : ''), children: row.kind === 'create' ? (_jsxs(_Fragment, { children: [_jsx(Plus, { size: 14, "aria-hidden": "true", className: "text-primary" }), _jsx("span", { className: "truncate", children: createLabel?.(text.trim()) })] })) : (_jsxs(_Fragment, { children: [row.option.adornment, _jsx("span", { className: "min-w-0 flex-1 truncate", children: row.option.label })] })) }, row.kind === 'create' ? '__create' : row.option.value)))) }) })] }));
}
//# sourceMappingURL=form.js.map